import { AdminLayout } from "@/project/jain_elibrary-admin/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  adminService,
  Book,
} from "@/project/jain_elibrary-admin/services/adminService";
import { Loader2, Plus, RefreshCw, Star, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

/** Matches public home featured strip (`Index.tsx` uses `slice(0, 8)`). */
const MAX_FEATURED = 8;

const FeaturedBooks = () => {
  const [featured, setFeatured] = useState<Book[]>([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Book[]>([]);
  const [searching, setSearching] = useState(false);
  const [pendingSr, setPendingSr] = useState<string | null>(null);

  const loadFeatured = useCallback(async () => {
    try {
      setLoadingFeatured(true);
      const list = await adminService.getFeaturedBooks();
      setFeatured(list);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      console.error(error);
      toast.error(
        err?.response?.data?.detail ??
          "Could not load featured books. Check API permissions and that GET /books/featured/ is available."
      );
      setFeatured([]);
    } finally {
      setLoadingFeatured(false);
    }
  }, []);

  useEffect(() => {
    void loadFeatured();
  }, [loadFeatured]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          setSearching(true);
          const res = await adminService.getBooks({
            search: q,
            page_size: 20,
          });
          setSearchResults(res.results ?? []);
        } catch (error: unknown) {
          const err = error as { response?: { data?: { detail?: string } } };
          console.error(error);
          toast.error(
            err?.response?.data?.detail ?? "Search failed. Try again."
          );
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const featuredSrSet = new Set(featured.map(b => b.sr_no));

  const setFeaturedFlag = async (srNo: string, is_featured: boolean) => {
    try {
      setPendingSr(srNo);
      await adminService.patchBook(srNo, { is_featured });
      toast.success(
        is_featured
          ? "Added to featured books."
          : "Removed from featured books."
      );
      await loadFeatured();
      if (!is_featured) {
        setSearchResults(prev =>
          prev.map(b => (b.sr_no === srNo ? { ...b, is_featured: false } : b))
        );
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: Record<string, unknown> | string; status?: number };
      };
      console.error(error);
      const detail =
        typeof err?.response?.data === "object" && err.response.data
          ? JSON.stringify(err.response.data)
          : (err?.response?.data as string | undefined);
      toast.error(
        detail ??
          "Update failed. Ensure PATCH /books/{sr_no}/feature/ is allowed for approved admins."
      );
    } finally {
      setPendingSr(null);
    }
  };

  return (
    <AdminLayout>
      <div className="min-w-0 space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold leading-tight text-foreground sm:text-3xl">
              Featured books (home page)
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Choose which books appear in the public site “Featured” section.
              Up to {MAX_FEATURED} are shown on the home page.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void loadFeatured()}
            disabled={loadingFeatured}
            className="shrink-0 gap-2"
          >
            {loadingFeatured ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>

        <section className="min-w-0 rounded-lg border border-border bg-card p-4 shadow-sm sm:p-6">
          <div className="mb-5 flex items-center gap-2">
            <Star className="h-5 w-5 shrink-0 text-amber-500" />
            <h3 className="text-lg font-semibold leading-tight">
              Current featured ({featured.length}/{MAX_FEATURED})
            </h3>
          </div>
          {loadingFeatured ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : featured.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No featured books yet. Search below and add titles.
            </p>
          ) : (
            <div className="-mx-1 overflow-x-auto px-1 sm:mx-0 sm:px-0">
              <table className="w-full min-w-[640px] table-fixed border-collapse text-left text-sm">
                <thead>
                  <tr className="text-muted-foreground">
                    <th
                      scope="col"
                      className="w-[8.5rem] border-b border-border pb-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide sm:w-36 sm:pr-4"
                    >
                      Sr. no.
                    </th>
                    <th
                      scope="col"
                      className="border-b border-border pb-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide sm:pr-4"
                    >
                      Title
                    </th>
                    <th
                      scope="col"
                      className="w-[26%] border-b border-border pb-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide sm:pr-4"
                    >
                      Author
                    </th>
                    <th
                      scope="col"
                      className="w-[6.5rem] border-b border-border pb-3 pl-2 text-right text-xs font-semibold uppercase tracking-wide sm:w-28"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {featured.map((book, index) => (
                    <tr
                      key={book.sr_no}
                      className="border-b border-border/60 last:border-0"
                    >
                      <td className="py-3 pr-3 align-top font-mono text-xs sm:pr-4">
                        {index + 1}. {book.sr_no}
                      </td>
                      <td className="min-w-0 break-words py-3 pr-3 align-top sm:pr-4">
                        {book.title_english}
                        {book.title_language ? (
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {book.title_language}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 pr-3 align-top text-muted-foreground sm:pr-4">
                        {book.author ?? "—"}
                      </td>
                      <td className="py-3 pl-2 text-right align-top">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="gap-1 text-destructive hover:text-destructive"
                          disabled={pendingSr === book.sr_no}
                          onClick={() =>
                            void setFeaturedFlag(book.sr_no, false)
                          }
                        >
                          {pendingSr === book.sr_no ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="min-w-0 rounded-lg border border-border bg-card p-4 shadow-sm sm:p-6">
          <h3 className="text-lg font-semibold leading-tight">Add books</h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Type at least 2 characters to search the catalogue. Books already
            featured are hidden from results.
          </p>
          <div className="relative mt-6 max-w-xl">
            <Input
              placeholder="Search by title, author, serial number…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pr-9"
            />
            {searching ? (
              <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            ) : null}
          </div>
          {search.trim().length > 0 && search.trim().length < 2 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Enter at least 2 characters to search.
            </p>
          ) : null}
          {searchResults.length > 0 ? (
            <div className="-mx-1 mt-8 overflow-x-auto px-1 sm:mx-0 sm:px-0">
              <table className="w-full min-w-[640px] table-fixed border-collapse text-left text-sm">
                <thead>
                  <tr className="text-muted-foreground">
                    <th
                      scope="col"
                      className="w-[8.5rem] border-b border-border pb-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide sm:w-36 sm:pr-4"
                    >
                      Sr. no.
                    </th>
                    <th
                      scope="col"
                      className="border-b border-border pb-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide sm:pr-4"
                    >
                      Title
                    </th>
                    <th
                      scope="col"
                      className="w-[26%] border-b border-border pb-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide sm:pr-4"
                    >
                      Author
                    </th>
                    <th
                      scope="col"
                      className="w-[6.5rem] border-b border-border pb-3 pl-2 text-right text-xs font-semibold uppercase tracking-wide sm:w-28"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults
                    .filter(b => !featuredSrSet.has(b.sr_no))
                    .map(book => {
                      const atCap = featured.length >= MAX_FEATURED;
                      return (
                        <tr
                          key={book.sr_no}
                          className="border-b border-border/60 last:border-0"
                        >
                          <td className="py-3 pr-3 align-top font-mono text-xs sm:pr-4">
                            {book.sr_no}
                          </td>
                          <td className="min-w-0 break-words py-3 pr-3 align-top sm:pr-4">
                            {book.title_english}
                          </td>
                          <td className="py-3 pr-3 align-top text-muted-foreground sm:pr-4">
                            {book.author ?? "—"}
                          </td>
                          <td className="py-3 pl-2 text-right align-top">
                            <Button
                              type="button"
                              size="sm"
                              className="gap-1"
                              disabled={atCap || pendingSr === book.sr_no}
                              title={
                                atCap
                                  ? `Maximum ${MAX_FEATURED} featured books for the home page`
                                  : undefined
                              }
                              onClick={() =>
                                void setFeaturedFlag(book.sr_no, true)
                              }
                            >
                              {pendingSr === book.sr_no ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                              Add
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ) : search.trim().length >= 2 && !searching ? (
            <p className="mt-6 text-sm text-muted-foreground">
              No matching books found, or all matches are already featured.
            </p>
          ) : null}
        </section>
      </div>
    </AdminLayout>
  );
};

export default FeaturedBooks;
